import { fetchFastApi, ApiError } from "../api/fastapi";
import {
  EvidenceConfirmStatusSchema,
  EvidenceRunStateEnvelopeSchema,
  EvidenceUploadPreviewSchema,
  type EvidenceRunState,
  type EvidenceUploadPreview,
  type EvidenceUploadResult,
} from "../models/audit/evidenceUpload";

// Both halves of a zip upload run on a worker, so the client polls for their
// outcome rather than holding one request open for the whole job. Holding it
// open is what used to break: on a large archive the request outlived the load
// balancer's idle timeout and the browser reported a failure while the work ran
// on to completion, so the auditor uploaded again and created duplicates.

// Short enough that a small archive still feels immediate.
const FAST_POLL_INTERVAL_MS = 2000;

// Runs that get past this are the long ones; a slower cadence keeps an hour of
// waiting to a few hundred requests rather than a few thousand.
const SLOW_POLL_INTERVAL_MS = 10000;
const FAST_POLL_WINDOW_MS = 30000;

// A poll that comes back without an answer about the run says nothing about the
// run — that is the very failure this flow exists to stop misreporting.
// Consecutive drops are tolerated; only a sustained run of them is fatal.
const MAX_CONSECUTIVE_DROPPED_POLLS = 5;

// The parse worker allows five minutes queued plus ten minutes running, so a
// client that gave up sooner would abandon a run the server still honours.
const PARSE_DEADLINE_MS = 15 * 60 * 1000;

// The confirm worker allows five minutes queued plus an hour running: on the
// legacy pipeline the run also does the extraction and matching inline. The
// backend gives the record that finds the run a longer life still
// (ACTIVE_RUN_TTL_SECONDS), so the lookup outlives every poll made here.
const CONFIRM_DEADLINE_MS = 65 * 60 * 1000;

export class EvidenceRunTimeoutError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "EvidenceRunTimeoutError";
  }
}

export class EvidenceRunFailedError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "EvidenceRunFailedError";
  }
}

interface PollEvidenceRunOptions<T> {
  path: string;
  deadlineMs: number;
  failureMessage: string;
  timeoutMessage: string;
  // Reads the finished run's payload out of the poll body. Called only once the
  // run reports `succeeded`.
  readResult: (body: unknown) => T;
  // Aborted when the caller no longer wants the outcome. Checked between polls,
  // so an abort stops the loop rather than cancelling an in-flight request.
  signal?: AbortSignal;
  // Called with the run's state on every poll. The wait spans a queue and
  // then the work, and "nothing has picked this up yet" is worth telling the
  // auditor apart from "it is being read right now".
  onState?: (state: EvidenceRunState) => void;
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Poll a dispatched evidence run until it finishes.
 *
 * Resolves with the run's payload once it reports `succeeded`, or null if the
 * caller aborted. Rejects with the backend's own message when the archive is
 * one the auditor must fix (a 400), with {@link EvidenceRunFailedError} when
 * the run itself failed, and with {@link EvidenceRunTimeoutError} when it
 * outlives the worker's own limits. A dropped poll is retried rather than
 * reported as a failed run.
 */
async function pollEvidenceRun<T>({
  path,
  deadlineMs,
  failureMessage,
  timeoutMessage,
  readResult,
  signal,
  onState,
}: PollEvidenceRunOptions<T>): Promise<T | null> {
  const startedAt = Date.now();
  const deadline = startedAt + deadlineMs;
  let consecutiveDroppedPolls = 0;

  while (Date.now() < deadline) {
    if (signal?.aborted) return null;

    try {
      const response = await fetchFastApi({ path, method: "GET" });
      consecutiveDroppedPolls = 0;

      const { state } = EvidenceRunStateEnvelopeSchema.parse(response);
      onState?.(state);
      if (state === "succeeded") return readResult(response);
      if (state === "failed") throw new EvidenceRunFailedError(failureMessage);
    } catch (error) {
      if (error instanceof EvidenceRunFailedError) throw error;

      // A rejected archive (unsupported file types, not a zip, too large) is
      // the auditor's to fix and will not change on the next poll.
      if (error instanceof ApiError && error.status === 400) throw error;

      // A poll that dies at the network or gateway level never reached the
      // run. Neither did a 404: the lookup from upload id to run is a Redis
      // record with its own lifetime, so it can go while the run it points at
      // is still going, and "no record" is not "the run failed". Calling
      // either one a failure is what makes an auditor confirm the same upload
      // twice, which is the bug this flow exists to fix.
      const droppedPoll =
        error instanceof ApiError &&
        (error.isNetworkError ||
          error.status === 404 ||
          error.status === 502 ||
          error.status === 504);
      if (!droppedPoll) throw error;

      consecutiveDroppedPolls += 1;
      if (consecutiveDroppedPolls >= MAX_CONSECUTIVE_DROPPED_POLLS) {
        throw error;
      }
    }

    await sleep(
      Date.now() - startedAt < FAST_POLL_WINDOW_MS
        ? FAST_POLL_INTERVAL_MS
        : SLOW_POLL_INTERVAL_MS,
    );
  }

  if (signal?.aborted) return null;
  throw new EvidenceRunTimeoutError(timeoutMessage);
}

/**
 * Poll a dispatched parse until its preview is ready. `sessionApiBase` is the
 * session's evidence-route prefix (see `controlsV2SessionApiBase`), so the same
 * poll serves a control or a work-step session.
 */
export function pollEvidencePreview(options: {
  sessionApiBase: string;
  uploadId: string;
  signal?: AbortSignal;
  onState?: (state: EvidenceRunState) => void;
}): Promise<EvidenceUploadPreview | null> {
  return pollEvidenceRun({
    path: `${options.sessionApiBase}/evidence/preview/${options.uploadId}`,
    deadlineMs: PARSE_DEADLINE_MS,
    failureMessage: "Analyzing the ZIP file failed",
    timeoutMessage: "Timed out waiting for the ZIP file to be analyzed",
    // A succeeded poll carries the preview in the same body.
    readResult: (body) => EvidenceUploadPreviewSchema.parse(body),
    signal: options.signal,
    onState: options.onState,
  });
}

/** Poll a dispatched confirm until its documents have been created. */
export function pollEvidenceConfirm(options: {
  sessionApiBase: string;
  uploadId: string;
  signal?: AbortSignal;
}): Promise<EvidenceUploadResult | null> {
  return pollEvidenceRun({
    path: `${options.sessionApiBase}/evidence/confirm/${options.uploadId}/status`,
    deadlineMs: CONFIRM_DEADLINE_MS,
    failureMessage: "Processing the ZIP file failed",
    timeoutMessage: "Timed out waiting for the ZIP file to be processed",
    readResult: (body) => {
      const status = EvidenceConfirmStatusSchema.parse(body);
      if (!status.result) {
        // A succeeded run always carries its counts; without them there is
        // nothing to report and treating it as success would be a lie.
        throw new EvidenceRunFailedError("Processing the ZIP file failed");
      }
      return status.result;
    },
    signal: options.signal,
  });
}
